import { Fragment } from 'react'
import { PlusIcon, XIcon } from 'lucide-react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { RHFInlineEditText } from '@/shared/components/HookForm'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardAction,
	CardContent,
	CardHeader,
} from '@/shared/components/ui/card'

import { AssignmentFields } from './AssignmentFields'

type AssignmentFieldsControllerProps = {
	/**
	 * The name of the field in the form
	 * @example 'settings.product_assignments'
	 */
	name: string
}
export const AssignmentFieldsController = ({
	name,
}: AssignmentFieldsControllerProps) => {
	const { t } = useTranslation()
	const { control, getFieldState } = useFormContext()
	const rootError = getFieldState(name)?.error?.root?.message
	const { fields, append, remove } = useFieldArray({
		name,
	})

	return (
		<div className='relative space-y-2'>
			{!!rootError && (
				<Alert variant='destructive'>
					<AlertDescription>{rootError}</AlertDescription>
				</Alert>
			)}
			{fields?.map((item, index) => (
				<Fragment key={item.id}>
					<Card className='shadow-none'>
						<CardHeader className='grid-rows-1 pb-0'>
							<RHFInlineEditText
								className='text-md mb-0 w-full font-bold'
								name={`${name}.${index}.name`}
								placeholder={t('pc.assignment.segment', {
									defaultValue: 'Segment',
								})}
							/>
							<CardAction>
								<Button
									data-testid='remove-segment-button'
									variant='ghost'
									size='icon'
									onClick={() => remove(index)}
								>
									<XIcon />
								</Button>
							</CardAction>
						</CardHeader>
						<CardContent>
							<Controller
								name={`${name}.${index}`}
								control={control}
								render={({
									field: { value, onChange },
									fieldState: { error },
								}) => (
									<AssignmentFields
										formData={value}
										error={error}
										onChange={onChange}
									/>
								)}
							/>
						</CardContent>
					</Card>
					{index < fields.length - 1 && (
						<div className='flex items-center justify-center gap-2 text-center text-sm'>
							OR
						</div>
					)}
				</Fragment>
			))}
			<Button
				data-testid='add-segment-button'
				variant='ghost'
				onClick={() =>
					append({
						name: 'Segment',
						filters: [
							{
								name: '',
							},
						],
					})
				}
			>
				<PlusIcon />
				{t('pc.assignment.add_segment', {
					defaultValue: 'Add segment',
				})}
			</Button>
		</div>
	)
}
